import React, { Suspense } from 'react';
import { DownloadIcon } from 'lucide-react';
import { formatDate } from 'venn-components';
import { useHasFF } from 'venn-utils';
import { type DateRange } from 'venn-ui-kit';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContentWithArrow, TooltipTrigger } from '@/components/ui/tooltip';
import { useComplianceMetadataExport } from '../useComplianceMetadataExport';

type Props = { dateRange: DateRange; disabled: boolean };

const InnerButton = ({ dateRange, disabled }: Props) => {
  const { hasExportPermission, exportMetadata } = useComplianceMetadataExport();
  const isDisabled = disabled || !hasExportPermission;
  return (
    <Tooltip>
      <TooltipTrigger>
        <Button variant="outline" size="xl" disabled={isDisabled} onClick={() => exportMetadata(dateRange)}>
          <DownloadIcon />
          Export metadata
        </Button>
      </TooltipTrigger>
      <TooltipContentWithArrow side="top">
        {!hasExportPermission ? (
          <span>You do not have permission to export metadata</span>
        ) : (
          <>
            <span>For selected date range:&nbsp;</span>
            <span>
              {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
            </span>
          </>
        )}
      </TooltipContentWithArrow>
    </Tooltip>
  );
};

const LoadingButton = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span>
        <Button variant="outline" size="xl" disabled>
          <DownloadIcon />
          Export metadata
        </Button>
      </span>
    </TooltipTrigger>
    <TooltipContentWithArrow side="top">Loading metadata…</TooltipContentWithArrow>
  </Tooltip>
);

export const ExportMetadataButton = (props: Props) => {
  const canExport = useHasFF('compliance_metadata_export_ff');
  if (!canExport) return null;
  return (
    <Suspense fallback={<LoadingButton />}>
      <InnerButton {...props} />
    </Suspense>
  );
};
